import { useState, useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import {
  useOverviewStats,
  useTrafficData,
  useRevenueData,
  useBookingFunnel,
  useAdPerformance,
  getDateRangeFromPreset,
  type DateRangePreset,
} from '@/hooks/useAnalytics'
import {
  DateRangeSelector,
  StatsOverview,
  TrafficChart,
  RevenueChart,
  BookingFunnel,
  AdPerformanceTable,
} from '@/components/analytics'

export function UserDashboardPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('7d')

  // Use impersonated account when impersonating, otherwise use real account
  const displayAccount = isImpersonating ? impersonatedAccount : account

  // Convert preset to date range
  const dateRange = useMemo(
    () => getDateRangeFromPreset(dateRangePreset),
    [dateRangePreset]
  )

  // Fetch analytics data
  const { data: stats, isLoading: statsLoading } = useOverviewStats(dateRange)
  const { data: trafficData, isLoading: trafficLoading } = useTrafficData(dateRange)
  const { data: revenueData, isLoading: revenueLoading } = useRevenueData(dateRange)
  const { data: funnelData, isLoading: funnelLoading } = useBookingFunnel(dateRange)
  const { data: adData, isLoading: adLoading } = useAdPerformance(dateRange)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Welcome, {displayAccount?.name || 'Dashboard'}
          </h1>
          <p className="text-muted-foreground">
            Monitor your website performance and booking analytics
          </p>
        </div>
        <DateRangeSelector value={dateRangePreset} onChange={setDateRangePreset} />
      </div>

      {/* Stats Overview Cards */}
      <StatsOverview stats={stats} isLoading={statsLoading} />

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        <TrafficChart data={trafficData} isLoading={trafficLoading} />
        <RevenueChart data={revenueData} isLoading={revenueLoading} />
      </div>

      {/* Funnel and Ad Performance Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        <BookingFunnel data={funnelData} isLoading={funnelLoading} />
        <AdPerformanceTable data={adData} isLoading={adLoading} />
      </div>
    </div>
  )
}
